/* eslint-disable react/button-has-type */
import classNames from 'classnames';
import Color from 'color';
import { LocationDescriptorObject } from 'history';
import React, {
  ElementType,
  memo,
  MouseEventHandler,
  ReactNode,
  useMemo
} from 'react';
import styled from 'styled-components';
import { usePayStack, useRouteLink } from '../helpers/hooks';
import { isValidColor } from '../helpers/utils';
import { Styled } from '../types';
import Loader from './Loader';
import ModalLink from './Modal/ModalLink';

window.Color = Color;

export interface ButtonProps extends Styled {
  type?: string;
  as?: 'button' | 'Link' | 'a' | 'label';
  color?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'tertiary' | 'custom';
  block?: boolean;
  disabled?: boolean;
  loading?: boolean;
  icon?: ElementType;
  children?: ReactNode;
  rounded?: boolean;
  caps?: boolean;
  buttonType?: string;
  id?: string;
  bordered?: boolean;
  link?: string;
  linkData?: { [key: string]: any };
  responsive?: boolean;
  to?: LocationDescriptorObject;
  onClick?: MouseEventHandler;
  variantType?: string;
}

const Button = ({
  type = 'button',
  icon,
  loading = false,
  color = '',
  as = 'button',
  id = '',
  caps = false,
  variant = 'primary',
  size = 'lg',
  block = false,
  children = null,
  className = '',
  rounded = true,
  bordered = false,
  buttonType = '',
  link = '',
  linkData = {},
  disabled = false,
  variantType,
  responsive = true,
  ...restProps
}: ButtonProps) => {
  let variantClassName;
  switch (variant) {
    case 'primary':
      variantClassName = classNames(
        `${color || (loading ? 'bg-primary-500' : 'bg-primary-450')}`,
        'font-bold text-white',
        { 'hover:bg-primary-500': !loading && !disabled }
      );
      break;
    case 'secondary':
      variantClassName = classNames(
        `${color || (loading ? 'text-primary-500' : 'text-primary-450')}`,
        'bg-transparent font-normal',
        { 'hover:text-primary-500': !loading && !disabled }
      );
      break;
    case 'custom':
      variantClassName = classNames(
        `${
          variantType === 'flexNaira'
            ? 'variantType--flexNaira'
            : 'text-red-450'
        }`,
        'font-normal',
        { 'hover:text-white-700': !loading && !disabled }
      );
      break;
    default:
      variantClassName = classNames(
        `${color || (loading ? 'text-gray-700' : 'text-gray-600')}`,
        'bg-transparent font-normal',
        { 'hover:text-gray-700': !loading }
      );
  }

  const linkTo = useRouteLink();
  const { pay, loading: paymentLoading } = usePayStack();
  const [Clickable, otherProps] = useMemo(() => {
    let dClickable;
    const to = linkTo(link, linkData);
    const dOtherProps: { [key: string]: any } = { ...restProps };
    if (to && to.pathname) {
      dClickable = ModalLink;
      dOtherProps.to = to;
    } else if (to && to.state && to.state.routeId === 'Pay') {
      dClickable = 'button';
      dOtherProps.type = 'button';
      dOtherProps.onClick = pay(to.state);
      dOtherProps.loading = paymentLoading;
    } else if (as === 'Link') {
      dClickable = ModalLink;
    } else if (as === 'a') {
      dClickable = 'a';
    } else if (as === 'label') {
      dClickable = 'label';
    } else {
      dClickable = 'button';
      dOtherProps.type = type;
    }
    return [dClickable, dOtherProps];
  }, [linkTo, link, linkData, restProps, as, pay, paymentLoading, type]);
  const StyledClickable = useMemo(
    () => styled(Clickable as ElementType)`
      ${isValidColor(color) && variant === 'primary'
        ? `
      background-color: ${color}
      &:hover {
        ${!disabled ? `background-color: ${Color(color).darken(0.3)}` : ''}
      }
    `
        : ''}
      ${isValidColor(color) && variant !== 'primary'
        ? `
      color: ${color}
      border-color: ${color}
      &:hover {
        ${!disabled ? `color: ${Color(color).darken(0.3)}` : ''}
      }
    `
        : ''}
    `,
    [Clickable, color, variant, disabled]
  );

  return (
    <StyledClickable
      className={classNames(
        'block disabled:opacity-75 disabled:cursor-not-allowed text-center whitespace-no-wrap',
        { 'rounded-t rounded-br': rounded },
        { 'border border-primary': bordered },
        { 'w-full': block },
        { 'flex flex-col md:flex-row items-center justify-center': responsive },
        {
          'py-0 px-3 md:px-5 font-body text-xs md:text-sm': size === 'xs',
          'py-1 px-3 md:px-5 font-body text-xs md:text-sm': size === 'sm',
          'pt-3 py-2 px-8 font-display text-xs md:text-sm uppercase':
            size === 'md',
          'pt-4 py-3 px-10 font-display text-sm md:text-sm uppercase':
            size === 'lg'
        },
        variantClassName,
        className
      )}
      disabled={disabled}
      {...otherProps}
    >
      {(() => {
        if (!icon) {
          return null;
        }
        const Icon = icon;
        return (
          <Icon
            className={classNames(
              'mb-1 md:mb-0 mr-0 md:mr-4 text-lg md:text-2xl',
              { 'inline mr-2': !responsive }
            )}
          />
        );
      })()}
      {loading ? <Loader /> : children}
    </StyledClickable>
  );
};

export default memo(Button);
